import type { ComponentProps, FC } from "react";
import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import queryString from "query-string";

import { Table } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";

import { SearchKeywordCell } from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";
import type { ActivationObjectSchema } from "app/pages/activation/types/api";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

import { useMonitorKeywordsEndpoints } from "../../monitorKeywordsEndpoints";

import { getColumns } from "./columnsConfig";
import { EnrichIndexContainer, EnrichTopContainer, EnrichDataContainer, EnrichPageContainer } from "./StyledComponents";
import type { IActivationSetCellEnrichedResponse } from "./types";
import { transformEnrichedCellData } from "./utils";
const DEFAULT_SORT = "clicks";
const DEFAULT_ASC = false;
const ROWS_PER_PAGE = 5;

interface IActivationSetCellEnriched {
    row: { keyword: string; index: number };
    activationSetDomains: ActivationObjectSchema["allDomains"];
}

export const ActivationSetCellEnriched: FC<IActivationSetCellEnriched> = ({ row, activationSetDomains }) => {
    const { keyword, index } = row;
    const { country } = useCurrentStateAndParams().params;
    const endpoints = useMonitorKeywordsEndpoints();
    const [currentPage, setCurrentPage] = useState(1);
    const [sort, setSort] = useState<ISortProps>({
        sort: DEFAULT_SORT,
        asc: DEFAULT_ASC,
    });

    const apiParams = {
        sort: sort.sort,
        asc: sort.asc,
        currentPage,
    };

    const requestBody = {
        country,
        keyword,
        sitesFilterExact: activationSetDomains,
    };

    const closeEnrichedRow = () => {
        allTrackers.dispatchTrackEvent("Open", "Click", "Project Leader Drilldown/Collapsed");
        document.body.click();
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: [endpoints.domainsDrilldown, country, sort.asc, sort.sort, currentPage, keyword, activationSetDomains.join(",")],
        queryFn: async () =>
            DefaultFetchService.getInstance().post<IActivationSetCellEnrichedResponse>(
                `${endpoints.domainsDrilldown}?${queryString.stringify(apiParams)}`,
                requestBody,
            ),
        select: transformEnrichedCellData,
    });

    const onSort: ComponentProps<typeof Table<(typeof data.data)[number]>>["onSort"] = (sorter) => {
        const isAsc = sorter.order === "ascend";
        setSort({
            sort: sorter.columnKey as string,
            asc: isAsc,
        });
        setCurrentPage(1);
    };

    const noDataObject = isError
        ? {
              title: translate("keyword.research.search.ads.table.error.title"),
              subtitle: translate("keyword.research.search.ads.table.error.subtitle"),
          }
        : {
              title: translate("keyword.research.search.ads.table.no.data.title"),
              subtitle: translate("keyword.research.search.ads.table.no.data.subtitle"),
          };

    const onItemClick = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `analyze keyword/${keyword}`);
    };

    return (
        <>
            <EnrichTopContainer data-automation="activation-set-cell-enriched-row">
                <EnrichDataContainer>
                    <EnrichIndexContainer>{(currentPage - 1) * ROWS_PER_PAGE + (index + 1)}</EnrichIndexContainer>
                    <EnrichPageContainer className={"swReactTableCell"}>
                        <SearchKeywordCell
                            value={keyword}
                            onItemClick={onItemClick}
                        />
                    </EnrichPageContainer>
                </EnrichDataContainer>
                <IconButton
                    type={buttonTypes.FLAT}
                    onClick={closeEnrichedRow}
                    iconName="clear"
                    placement="left"
                    iconSize="sm"
                />
            </EnrichTopContainer>
            <Table<(typeof data.data)[number]>
                columnConfig={getColumns(data?.data?.length ?? 0)}
                data={data?.data ?? []}
                isLoading={isLoading}
                loaderRowCount={ROWS_PER_PAGE}
                noData={noDataObject}
                size="large"
                pagination={{
                    pageSize: ROWS_PER_PAGE,
                    total: data?.totalCount ?? 0,
                    current: currentPage,
                    onChange: (_, __, newPage) => setCurrentPage(newPage),
                }}
                sticky={false}
                onSort={onSort}
            />
        </>
    );
};
